import { useState } from "react";
import { useHistory } from "react-router-dom";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { useWorkflowStore } from "./workflowStore";
import { WORKFLOW_BLOCK_TYPE } from "../../../../../../src/shared/types/workflows";
import VersionHeader from "./VersionHeader";
import LiquidDisplay from "./LiquidDisplay";
import { useToast } from "src/components/ToastWrapper/index.js";

function ApiBlock({ block }) {
  return (
    <div className="flex flex-col gap-2 mt-3">
      {block.options.variables.map((variable) => {
        return (
          <span key={variable.id}>
            <strong className="font-mono">{variable.name}</strong> <span className="text-gray-600">- {variable.description}</span>
          </span>
        );
      })}
    </div>
  );
}

function InferenceBlock({ block }) {
  return (
    <div>
      {block.options.prompt.messages.map((message) => {
        return (
          <div key={message.id}>
            <h4 className="text-sm font-bold">{message.role}</h4>

            <LiquidDisplay template={message.template} />
          </div>
        );
      })}
      <h3 className="mt-3">Parameters</h3>
      <div className="flex flex-row flex-wrap gap-2 mt-3">
        {block.options.engine && (
          <div className="flex flex-row gap-2">
            <span>engine</span>
            <strong>{block.options.engine}</strong>
          </div>
        )}
        {block.options.frequencyPenalty !== undefined && (
          <div className="flex flex-row gap-2">
            <span>frequency penalty</span>
            <strong>{block.options.frequencyPenalty}</strong>
          </div>
        )}
        {block.options.maxTokens !== undefined && (
          <div className="flex flex-row gap-2">
            <span>max tokens</span>
            <strong>{block.options.maxTokens}</strong>
          </div>
        )}
        {block.options.stop && (
          <div className="flex flex-row gap-2">
            <span>stop sequences</span>
            <strong>{block.options.stop}</strong>
          </div>
        )}
        {block.options.numGenerations !== undefined && (
          <div className="flex flex-row gap-2">
            <span>num generations</span>
            <strong>
              {typeof block.options.numGenerations === "object" && block.options.numGenerations?.type === "variable" ? `{{${block.options.numGenerations.value}}}` : block.options.numGenerations}
            </strong>
          </div>
        )}
        {block.options.stream !== undefined && (
          <div className="flex flex-row gap-2">
            <span>stream</span>
            <strong>{block.options.stream ? "true" : "false"}</strong>
          </div>
        )}
        {block.options.presencePenalty !== undefined && (
          <div className="flex flex-row gap-2">
            <span>presence penalty</span>
            <strong>{block.options.presencePenalty}</strong>
          </div>
        )}
        {block.options.temperature !== undefined && (
          <div className="flex flex-row gap-2">
            <span>temperature</span>
            <strong>{block.options.temperature}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

function ABTestBlock({ block, workflowId, version }) {
  const [running, setRunning] = useState(false);
  const [winnerName, setWinnerName] = useState(block.options.controlVariationName);
  const history = useHistory();
  const createNewVersion = useWorkflowStore((state) => state.createNewVersion);
  const workflow = useWorkflowStore((state) => state.workflows[workflowId]);
  const isProduction = workflow.productionVersionId === version.id;

  return (
    <div>
      <div className="flex flex-row flex-wrap gap-2">
        <div>
          Experiment Name: <strong>{block.options.experimentName}</strong>
        </div>
        <div>
          Population: <strong>{block.options.population}</strong>
        </div>
        <div>
          Percentage: <strong>{block.options.percentage}</strong>
        </div>
        <div>
          Skip Flagged Content: <strong>{block.options.skipFlaggedContent ? "Yes" : "No"}</strong>
        </div>
        <div>
          Assignment Strategy: <strong>{block.options.assignmentStrategy}</strong>
        </div>
        <div>
          Control Variation: <strong>{block.options.controlVariationName}</strong>
        </div>
        <div>
          Test Variation: <strong>{block.options.testVariationName}</strong>
        </div>
      </div>
      <div className="flex flex-row gap-2 items-center mt-3">
        {isProduction && (
          <>
            <div>
              <select
                value={winnerName}
                onChange={(e) => setWinnerName(e.target.value)}
              >
                <option value={block.options.controlVariationName}>{block.options.controlVariationName}</option>
                <option value={block.options.testVariationName}>{block.options.testVariationName}</option>
              </select>
            </div>
            <SudoButton
              className="mt-0"
              variant="primary"
              disabled={running}
              onClick={async () => {
                setRunning(true);
                const result = await createNewVersion(workflowId, version.id, block.options.experimentName, winnerName);
                setRunning(false);
                history.push(`/ai-functions/${workflowId}/v/${result.versionId}`);
              }}
            >
              Choose Winner: {winnerName}
            </SudoButton>
          </>
        )}
      </div>
    </div>
  );
}

function CloseRequestBlock({ block }) {
  return <div>Close the request without making any cards</div>;
}

function InputDocumentBlock({ block }) {
  return (
    <div>
      This provides <pre className="inline-block bg-gray-100 p-1 rounded-md">previous_document_text</pre> variable as well as set the document chiclets.
    </div>
  );
}

function CardOutputBlock({ block }) {
  return (
    <div>
      Create a card titled "{block.options.title}" and description based on the variable "{block.options.descriptionVariable}".
      {block.options.disableChiclets && (
        <div>
          <strong>Chiclets are disabled</strong>
        </div>
      )}
    </div>
  );
}

function OverrideDisplay({ override }) {
  const [isOptionChangesExpanded, setIsOptionChangesExpanded] = useState(false);

  const handleToggleOptionChanges = () => {
    setIsOptionChangesExpanded(!isOptionChangesExpanded);
  };

  return (
    <div className="border border-solid border-gray-300 rounded-md p-2 bg-gray-50">
      <div className="font-semibold text-sm">{override.name}</div>

      <div className="">
        {override.criteria &&
          override.criteria.map((criterion, index) => (
            <div
              key={criterion.id || index}
              className="text-sm"
            >
              When <strong>{criterion.options.variable}</strong> is <strong>{criterion.options.value}</strong>
            </div>
          ))}
      </div>

      {override.overrides && override.overrides.length > 0 && (
        <div className="bg-white rounded border">
          <SudoButton
            variant="tertiaryGrey"
            className="text-sm font-semibold text-blue-600 mt-0 hover:text-blue-800"
            onClick={handleToggleOptionChanges}
          >
            {isOptionChangesExpanded ? "Hide" : "Show"} Option Changes ({override.overrides.length})
          </SudoButton>
          {isOptionChangesExpanded && (
            <div className="space-y-1 p-2 pt-0">
              {override.overrides.map((overrideItem, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-sm"
                >
                  <span className="font-mono text-gray-600 text-xs">{overrideItem.path.replace("options.", "")}</span>
                  <span className="text-gray-500">→</span>
                  <span className="font-medium text-gray-800">{typeof overrideItem.value === "object" ? JSON.stringify(overrideItem.value) : String(overrideItem.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BlockDetails({ block, workflowId, version }) {
  if (block.blockType === WORKFLOW_BLOCK_TYPE.API_INPUT) {
    return <ApiBlock block={block} />;
  }

  if (block.blockType === WORKFLOW_BLOCK_TYPE.INFERENCE) {
    return <InferenceBlock block={block} />;
  }
  if (block.blockType === WORKFLOW_BLOCK_TYPE.CLOSE_REQUEST) {
    return <CloseRequestBlock block={block} />;
  }
  if (block.blockType === WORKFLOW_BLOCK_TYPE.AB_TEST) {
    return (
      <ABTestBlock
        block={block}
        workflowId={workflowId}
        version={version}
      />
    );
  }
  if (block.blockType === WORKFLOW_BLOCK_TYPE.INPUT_STORY_BIBLE) {
    return (
      <div>
        Story Bible variables are available.{" "}
        {block.options.disableChiclets && (
          <div>
            <strong>Chiclets are disabled</strong>
          </div>
        )}
      </div>
    );
  }
  if (block.blockType === WORKFLOW_BLOCK_TYPE.COST_CONTROLS) {
    return <div>Cost is {block.options.free ? "free" : "charged"}</div>;
  }
  if (block.blockType === WORKFLOW_BLOCK_TYPE.POV_AND_TENSE) {
    return <div>POV and Tense variables are available</div>;
  }
  if (block.blockType === WORKFLOW_BLOCK_TYPE.FEATURE_FLAGS) {
    return <div>These feature flags are available: {block.options.subscriptionFeatureFlags.map((flag) => flag.name).join(", ")}</div>;
  }
  if (block.blockType === WORKFLOW_BLOCK_TYPE.INPUT_DOCUMENT) {
    return <InputDocumentBlock block={block} />;
  }
  if (block.blockType === WORKFLOW_BLOCK_TYPE.CARD_OUTPUT) {
    return <CardOutputBlock block={block} />;
  }
  return <div>Unknown Block</div>;
}

function BlockTypeDisplay({ blockType }) {
  let label = blockType;
  if (blockType === WORKFLOW_BLOCK_TYPE.AB_TEST) {
    label = "A/B Test";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.CLOSE_REQUEST) {
    label = "Close Request";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.INFERENCE) {
    label = "Inference";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.API_INPUT) {
    label = "Variables";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.INPUT_DOCUMENT) {
    label = "Input Document";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.COST_CONTROLS) {
    label = "Cost Controls";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.INPUT_STORY_BIBLE) {
    label = "Story Bible";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.CARD_OUTPUT) {
    label = "Card Output";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.POV_AND_TENSE) {
    label = "POV and Tense";
  }
  if (blockType === WORKFLOW_BLOCK_TYPE.FEATURE_FLAGS) {
    label = "Feature Flags";
  }

  return <span className="text-gray-500 text-sm">{label}</span>;
}

function CompletedVersion({ workflowId, version }) {
  const history = useHistory();
  const { setToast } = useToast();
  const createNewVersion = useWorkflowStore((state) => state.createNewVersion);
  const blocks = version.definition.blocks;

  return (
    <div className="mt-3">
      <div className="flex justify-between items-center w-full">
        <VersionHeader
          workflowId={workflowId}
          version={version}
        />

        <SudoButton
          className="mt-0"
          variant="tertiary"
          onClick={async (e) => {
            e.preventDefault();
            const result = await createNewVersion(workflowId, version.id);
            history.push(`/ai-functions/${workflowId}/v/${result.versionId}`);
          }}
        >
          New Version From This
        </SudoButton>
      </div>

      <div className="flex flex-col gap-2 mt-3">
        {blocks.map((block) => {
          return (
            <div
              className="border border-solid border-black p-1 rounded-md"
              key={block.id}
            >
              <h3 className="text-lg font-bold">
                <BlockTypeDisplay blockType={block.blockType} />
              </h3>
              <BlockDetails
                block={block}
                workflowId={workflowId}
                version={version}
              />

              {block.overrides && block.overrides.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-md font-semibold mb-2">Overrides</h4>
                  <div className="flex flex-col gap-2">
                    {block.overrides.map((override) => (
                      <OverrideDisplay
                        key={override.id}
                        override={override}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 mb-10">
        <SudoButton
          variant="tertiary"
          className="mt-0 m-0 p-0 "
          onClick={() => {
            navigator.clipboard.writeText(JSON.stringify(version.definition.blocks, null, 2));
            setToast("Copied JSON to clipboard");
          }}
        >
          Copy Json
        </SudoButton>
      </div>
    </div>
  );
}

export default CompletedVersion;
